// University of Southern California: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - USC's Common Data Set 2025-2026 (Office of Institutional Research PDF,
//    CDS_2025-26_FINAL.pdf): C1 applicants/admits/enrollees for Fall 2025
//    with the residency table; C8 test policy for Fall 2027; C21/C22 early
//    plans; G1/G5 2026-2027 costs. The PDF's page footers still read "Common
//    Data Set 2024-2025"; its title page and contents (Fall 2025 counts,
//    2026-2027 costs) are 2025-2026.
//  - admission.usc.edu: First-Year Students (dates, binding terms, checklist,
//    the list of schools with extra requirements) and the Test Optional FAQ.
//    Its financial aid dates (November 1, 2026 to February 3, 2027) place it
//    in the fall 2027 cycle.
//  - School pages linked from that list, for what each asks of first-year
//    applicants: Cinematic Arts (freshman instructions), Thornton (how to
//    apply), Kaufman (how to apply), Roski (undergraduate admission).
//  - College Scorecard, school id 123961 (net price by family income), read
//    from the Scorecard site's own API response.
// Nothing here is profile-derived or anyone's odds. Re-check a
// figure against its source before editing it, and move `lastChecked` when
// you do.

export const SOURCES = {
    cds: {
        label: 'USC Common Data Set 2025-2026',
        url: 'https://oir.usc.edu/wp-content/uploads/sites/3/2026/10/CDS_2025-26_FINAL.pdf',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'USC Undergraduate Admission, First-Year Students',
        url: 'https://admission.usc.edu/prospective-students/how-to-apply/first-year-students/',
        read: '2026-09-30',
    },
    tests: {
        label: 'USC Undergraduate Admission, Test Optional FAQ',
        url: 'https://admission.usc.edu/test-optional-faq/',
        read: '2026-09-30',
    },
    cinema: {
        label: 'USC School of Cinematic Arts, Freshman Application Procedures',
        url: 'https://cinema.usc.edu/admissions/freshman.cfm',
        read: '2026-09-30',
    },
    thornton: {
        label: 'USC Thornton School of Music, Undergraduate Admissions',
        url: 'https://music.usc.edu/admissions-undergraduate/',
        read: '2026-09-30',
    },
    kaufman: {
        label: 'USC Kaufman School of Dance, How to Apply',
        url: 'https://kaufman.usc.edu/how-to-apply',
        read: '2026-09-30',
    },
    roski: {
        label: 'USC Roski School of Art and Design, Undergraduate Admission',
        url: 'https://roski.usc.edu/admissions/undergraduate-admission/',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?123961-University-of-Southern-California',
        read: '2026-10-15',
    },
    coa: {
        label: 'USC Financial Aid, 2026-2027 Estimate of Cost of Attendance',
        url: 'https://financialaid.usc.edu/undergraduate-financial-aid/cost-of-attendance/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-southern-california',
    kbId: 'university_of_southern_california',
    name: 'University of Southern California',
    shortName: 'USC',
    location: 'Los Angeles, CA',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (USC identity colors); decoration only.
    branding: {
        primary: '#990000',
        secondary: '#FFCC00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_southern_california_logo.png',
    },

    faq: {
        answer: { text: 'The University of Southern California admitted 11.2% of first-year applicants for fall 2025, 9,345 of the 83,488 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'Does USC require the SAT or ACT?', a: 'No. USC is test-optional: you decide whether your SAT or ACT scores are considered, and you are not penalized if you leave them out. If you want them considered, USC needs official scores sent by the testing agency.', source: 'tests' },
            { q: 'What is the USC acceptance rate for California residents?', a: 'For fall 2025, USC admitted 3,692 of 32,499 first-year applicants from California, 11.4%. For out-of-state applicants it was 9.8%, 3,378 of 34,374.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the USC acceptance rate for international students?', a: 'For fall 2025, USC admitted 2,275 of 16,615 international first-year applicants, 13.7%.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the acceptance rate for USC Marshall or Kaufman?', a: 'USC\'s Common Data Set does not give one. It reports a single first-year acceptance rate for the whole university, 11.2% for fall 2025, with no rate by school.', source: 'cds', where: 'section C1' },
            { q: 'What are USC\'s application deadlines for fall 2027?', a: 'Early Decision and Early Action are both November 1 for most majors. Regular Decision is January 10 for most majors and December 1 for dance, dramatic arts and music. Early Decision is binding; Early Action is not.', source: 'firstYear' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 83488,
        admitted: 9345,
        enrolled: 3759,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (California)', applied: 32499, admitted: 3692 },
            { group: 'Out-of-state', applied: 34374, admitted: 3378 },
            { group: 'International', applied: 16615, admitted: 2275 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'USC\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early rounds',
        note: 'For Fall 2025 entry the Common Data Set reports no early decision plan and has no field for early action counts, so it gives no early-round acceptance rate. Early decision is new since then (see the deadlines below).',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some USC majors ask for more than the Common App: a portfolio, a resume or writing samples, and auditions for some performance majors. What the schools named on USC\'s list ask of first-year applicants:',
        rows: [
            { school: 'School of Cinematic Arts', requirement: 'A SlideRoom application with supplemental materials for each program you apply to. After the deadline you get an interview and assessment invitation through Kira Talent, due within 5 business days.', source: 'cinema' },
            { school: 'Thornton School of Music', requirement: 'The USC application and a SlideRoom portfolio by December 1, then an audition and/or interview where your program requires one.', source: 'thornton' },
            { school: 'Kaufman School of Dance (BFA)', requirement: 'Materials, including a Kaufman portfolio, by December 1. Live auditions take place in January.', source: 'kaufman' },
            { school: 'Roski School of Art and Design', requirement: 'A portfolio, an artist statement and a list of creative accomplishments, with your USC application.', source: 'roski' },
            { school: 'School of Dramatic Arts', requirement: 'Its own requirements, with December 1 as the only deadline.', source: 'firstYear' },
        ],
        note: 'USC also lists extra requirements for the Iovine and Young Academy, the School of Architecture, and Marshall\'s World Bachelor in Business.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 10' },
        rows: [
            { plan: 'Early Decision (most majors)', deadline: 'November 1', decision: 'By mid-December', binding: true },
            { plan: 'Early Action (most majors)', deadline: 'November 1', decision: 'Late January', binding: false },
            { plan: 'Regular Decision (performing arts majors)', deadline: 'December 1', decision: 'By April 1', binding: false },
            { plan: 'Regular Decision (most majors)', deadline: 'January 10', decision: 'By April 1', binding: false },
        ],
        note: 'Dance, Dramatic Arts and Music majors apply only in Regular Decision, by December 1. Marshall\'s World Bachelor in Business takes applications only by November 1. Early applicants may be admitted or deferred to Regular Decision.',
        source: 'firstYear',
        binding: 'Early decision is binding. You may not apply early decision anywhere else, and if you are admitted you must enroll at USC and withdraw your other applications. You also submit an ED agreement.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'USC is test-optional. You choose whether your SAT or ACT is considered, and you are not penalized without it. If you send scores they are read, and they must be official scores from the testing agency. USC records your highest section scores across sittings.',
        source: 'tests',
        cdsLine: 'The Common Data Set agrees: for fall 2027, the SAT or ACT is not required but is considered if submitted, with scores due by February 20.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (12-18 units, two semesters)', amount: 75384 },
            { item: 'Fees', amount: 1952 },
            { item: 'Housing', amount: 13510 },
            { item: 'Food', amount: 8442 },
            { item: 'Books and supplies', amount: 670 },
            { item: 'Transportation', amount: 1188 },
            { item: 'Personal expenses', amount: 2016 },
        ],
        total: 103162,
        note: 'On campus. New students add a $450 fee in their first semester. Tuition is the same for in-state and out-of-state students.',
        source: 'coa',
        netPrice: {
            average: 33056,
            byIncome: [
                { income: '$0 to $30,000', amount: 17171 },
                { income: '$30,001 to $48,000', amount: 13736 },
                { income: '$48,001 to $75,000', amount: 18282 },
                { income: '$75,001 to $110,000', amount: 28033 },
                { income: 'Over $110,000', amount: 59062 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from USC\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
